/**
 * Anchor-based scroll persistence for the multi-view lobby
 *
 * An absolute scrollTop is unreliable here: sections that are not on screen render as
 * estimated-height placeholders, so the same pixel offset points at different content
 * before and after restoration. Anchoring to a section element plus an offset inside it
 * keeps the error bounded to that one section.
 *
 * Deliberately kept out of the scroll store: this is a single one-shot value handed from
 * game start to the next lobby boot, and the store's persisted blob is rejected on load
 * whenever the user has visited single view.
 */

interface IScrollAnchor {
    /** data-section-name of the section crossing the top edge of the lobby container */
    sectionName: string;
    /** How far the container is scrolled past that section's top edge, in pixels */
    offset: number;
}


const STORAGE_KEY = 'egtd_lobby_scroll_anchor';
// Must match exactly one element per section. `data-section-name` alone does not:
// ReactVirtualGrid puts it on the inner grid too, so save picked the inner element and
// restore picked the outer wrapper - a constant offset apart. `data-section-index` is
// unique to LazySectionWrapper.
const SECTION_SELECTOR = '[data-section-index][data-section-name]';

// Memoised per page load: the anchor is read-and-cleared, but StrictMode invokes state
// initialisers twice and the lobby may remount, so every caller must see the same value.
let consumedAnchor: { value: IScrollAnchor | null } | null = null;

function getRelativeTop(container: HTMLElement, element: HTMLElement): number {
  return element.getBoundingClientRect().top - container.getBoundingClientRect().top;
}

function getSectionElements(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(SECTION_SELECTOR));
}

/**
 * Capture the current scroll position as a section anchor and persist it.
 */
export function saveScrollAnchor(container: HTMLElement): void {
  const sections = getSectionElements(container);
  if (sections.length === 0) return;

  // The anchor is the last section whose top edge is at or above the container's
  // top edge - the one currently crossing the top of the viewport.
  let anchorElement = sections[0];
  for (const section of sections) {
    if (getRelativeTop(container, section) > 0) break;
    anchorElement = section;
  }

  const sectionName = anchorElement.dataset['sectionName'];
  if (!sectionName) return;

  const anchor: IScrollAnchor = {
    sectionName,
    offset: -getRelativeTop(container, anchorElement),
  };

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(anchor));
    consumedAnchor = null;
  } catch (err) {
    console.warn('Failed to save scroll anchor:', err);
  }
}

function readScrollAnchor(): IScrollAnchor | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    localStorage.removeItem(STORAGE_KEY);
    if (!stored) return null;

    const parsed = JSON.parse(stored);
    if (typeof parsed?.sectionName !== 'string' || !isFinite(parsed?.offset)) return null;

    return { sectionName: parsed.sectionName, offset: Math.max(0, parsed.offset) };
  } catch (err) {
    console.warn('Failed to read scroll anchor:', err);
    return null;
  }
}

/**
 * Read the stored anchor and clear it - restoration is one-shot per lobby boot.
 */
export function consumeScrollAnchor(): IScrollAnchor | null {
  if (!consumedAnchor) {
    consumedAnchor = { value: readScrollAnchor() };
  }

  return consumedAnchor.value;
}

/**
 * Translate an anchor back into a scrollTop against the current layout.
 * Returns null while the anchored section is not in the DOM yet.
 */
export function resolveAnchorPosition(container: HTMLElement, anchor: IScrollAnchor): number | null {
  const element = getSectionElements(container).find(
    (section) => section.dataset['sectionName'] === anchor.sectionName
  );
  if (!element) return null;

  return Math.max(0, container.scrollTop + getRelativeTop(container, element) + anchor.offset);
}
